import { useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router";

import { skipToken } from "@reduxjs/toolkit/query";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import AttorneyHubLogo from "_shared/_views/assets/attorney-hub-logo.svg";
import Loader from "_shared/_views/loading/loader";
import Guid from "_shared/guids/guid";
import { useGetOfficerInvitationByIdQuery } from "legal-entities/invitations/officer-invitations/_api/entity-officer-invitation-api-service";
import EntityOfficerInvitationAcceptanceForm from "legal-entities/invitations/officer-invitations/_views/components/entity-officer-invitation-acceptance-form";
import { EntityOfficerInvitationNotFoundError } from "legal-entities/invitations/officer-invitations/entity-officer-invitation";

const FormContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  boxSizing: "border-box",
  height: "100%",
  overflow: "auto",
  textAlign: "center",
  width: "100%",
}));
const BrandHeader = styled("header")(({ theme }) => ({
  backgroundColor: "#404558",
  padding: theme.spacing(2),
}));
const PageContent = styled("main")(({ theme }) => ({
  padding: theme.spacing(4, 2, 6),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2, 1, 4),
  },
}));
const Logo = styled("img")(({ theme }) => ({
  display: "block",
  height: theme.spacing(4.5),
  margin: "0 auto",
  maxWidth: "90%",
}));
const InvitationForm = styled(Container)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  color: theme.palette.text.primary,
  padding: theme.spacing(3),
  position: "relative",
  width: "100%",
}));
const InvitationFormTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));
const InvitationFormInstructions = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(2),
  "& a": {
    color: theme.palette.primary.main,
    textDecoration: "underline",
  },
}));

type OfficerInvitationPageProps = {
  className?: string;
  invitationId?: Guid;
};

export default function EntityOfficerInvitationPage(props: Readonly<OfficerInvitationPageProps>) {
  const { className } = props;

  const params = useParams<{ invitationId: string }>();
  // The emailed link carries the acceptance token in its query string (#6363).
  const [searchParams] = useSearchParams();
  const acceptanceToken = searchParams.get("token") ?? undefined;

  const invitationId = useMemo(
    () => props.invitationId ?? (params.invitationId ? new Guid(params.invitationId) : undefined),
    [params.invitationId, props.invitationId],
  );
  const { data: invitation, error, isFetching, isLoading, isUninitialized } = useGetOfficerInvitationByIdQuery(
    invitationId ? { id: invitationId } : skipToken,
  );
  const loading = Boolean(invitationId) && (isUninitialized || isLoading || isFetching);
  const invitationNotFound = !invitationId || error instanceof EntityOfficerInvitationNotFoundError;
  const invitationLoadError = Boolean(error) && !invitationNotFound;

  useEffect(() => {
    if (!invitationId) console.error(new Error("Invitation ID was not provided"));
  }, [invitationId]);

  useEffect(() => {
    if (!error) return;
    if (error instanceof EntityOfficerInvitationNotFoundError) {
      console.warn(error.message);
      return;
    }
    console.error(error);
  }, [error]);

  function invitationNotFoundMessage() {
    return (
      <InvitationForm className={className}>
        <InvitationFormTitle variant="h4">Invitation Not Found</InvitationFormTitle>
        <InvitationFormInstructions>
          The invitation couldn&apos;t be found.
          <br />
          If you believe this is in error please contact{" "}
          <a href="mailto:support@attorneyhub.com" target="_blank" rel="noreferrer">
            support@attorneyhub
          </a>
          .<br />
          You can close this tab
        </InvitationFormInstructions>
      </InvitationForm>
    );
  }

  function invitationErrorMessage() {
    return (
      <InvitationForm className={className}>
        <InvitationFormTitle variant="h4">Invitation Error</InvitationFormTitle>
        <InvitationFormInstructions>
          The invitation couldn&apos;t be loaded.
          <br />
          Please try again. If the problem continues, contact{" "}
          <a href="mailto:support@attorneyhub.com" target="_blank" rel="noreferrer">
            support@attorneyhub
          </a>
          .
        </InvitationFormInstructions>
      </InvitationForm>
    );
  }

  return (
    <FormContainer className={className}>
      <BrandHeader>
        <Logo src={AttorneyHubLogo} alt="Attorney Hub Logo" />
      </BrandHeader>
      <PageContent>
        {loading && <Loader />}
        {!invitation && !loading && invitationNotFound && invitationNotFoundMessage()}
        {!invitation && !loading && invitationLoadError && invitationErrorMessage()}
        {invitation && <EntityOfficerInvitationAcceptanceForm invitation={invitation} acceptanceToken={acceptanceToken} />}
      </PageContent>
    </FormContainer>
  );
}
